import { Controller, type ControllerProps } from "react-hook-form";
import React, { forwardRef, type ReactElement, type Ref } from "react";
import {
  RadioGroupV2,
  type RadioGroupV2Props,
} from "src/components/RadioGroupV2/RadioGroupV2.tsx";
import type { FieldPath, FieldValues } from "react-hook-form";
import { composeRefs } from "@exonn/hooks";
import type { RadioFieldOptions } from "src/components/Form/Form.types.ts";
import { getResourceFieldOptions } from "src/system/getResourceFieldOptions.ts";
import { useTranslation } from "react-i18next";

export type RadioFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
  options?: RadioFieldOptions;
} & Omit<RadioGroupV2Props, "defaultValue" | "options"> &
  Omit<ControllerProps<TFieldValues, TName>, "render">;

const RadioFieldImpl = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: RadioFieldProps<TFieldValues, TName>,
  ref: Ref<HTMLInputElement>,
) => {
  const {
    name,
    control,
    rules,
    shouldUnregister,
    defaultValue,
    disabled,
    options: optionsProp,
    onValueChange: onValueChangeProp,
    onBlur: onBlurProp,
    label,
    ...restProps
  } = props;

  const { t } = useTranslation();

  return (
    <Controller
      defaultValue={defaultValue}
      //  disabled={disabled}
      control={control}
      shouldUnregister={shouldUnregister}
      rules={rules}
      key={name}
      name={name}
      render={({ field: { name, value, onChange, onBlur, ref: fieldRef } }) => {
        const options = Array.isArray(optionsProp)
          ? optionsProp
          : optionsProp &&
            getResourceFieldOptions({
              resource: optionsProp?.resource,
              field: optionsProp?.field,
              t,
            });
        return (
          <RadioGroupV2
            label={label}
            name={name}
            disabled={disabled}
            onValueChange={(e) => {
              onChange(e);
              onValueChangeProp?.(e);
            }}
            onBlur={(e) => {
              onBlur();
              onBlurProp?.(e);
            }}
            ref={composeRefs(ref, fieldRef)}
            value={value}
            options={options}
            {...restProps}
          />
        );
      }}
    />
  );
};

export const RadioField = forwardRef(RadioFieldImpl) as <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: RadioFieldProps<TFieldValues, TName> & {
    ref?: Ref<HTMLInputElement>;
  },
) => ReactElement;
